import { API_CONFIG, STORAGE_KEYS } from '@shared/utils/constants';
import { retryWithBackoff } from '@shared/utils/helpers';
import tokenStorage from '@features/auth/utils/tokenStorage';

class ApiService {
  constructor() {
    this.baseUrl = API_CONFIG.BASE_URL;
    this.timeout = API_CONFIG.TIMEOUT;
  }

  // Get auth token (from secure in-memory storage)
  getAuthToken() {
    return tokenStorage.getAccessToken();
  }

  // Set auth token (store securely in memory)
  setAuthToken(token) {
    // Note: This only sets the access token in memory
    // The full token pair should be set via tokenStorage.setTokens()
    tokenStorage.accessToken = token;
  }

  // Clear auth token
  clearAuthToken() {
    tokenStorage.clearTokens();
  }

  // Get encrypted token
  getEncryptedToken() {
    return tokenStorage.getEncryptedToken();
  }

  // Create headers
  createHeaders(customHeaders = {}) {
    const headers = {
      'Content-Type': 'application/json',
      ...customHeaders,
    };

    const token = this.getAuthToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    // Add encrypted token for AI-Talent service
    const encryptedToken = this.getEncryptedToken();
    if (encryptedToken) {
      headers['X-Encrypted-Token'] = encryptedToken;
    }

    return headers;
  }

  // Fetch with timeout
  async fetchWithTimeout(url, options = {}, customTimeout = null) {
    const controller = new AbortController();
    const timeout = customTimeout || this.timeout;
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    try {
      const response = await fetch(url, {
        ...options,
        signal: controller.signal,
      });
      clearTimeout(timeoutId);
      return response;
    } catch (error) {
      clearTimeout(timeoutId);
      if (error.name === 'AbortError') {
        const err = new Error('REQUEST_TIMEOUT');
        err.code = 'TIMEOUT';
        err.userMessage = 'انتهت مهلة الطلب';
        throw err;
      }
      throw error;
    }
  }

  // Generic request method
  async request(endpoint, options = {}) {
    const url = `${this.baseUrl}${endpoint}`;
    const config = {
      ...options,
      headers: this.createHeaders(options.headers),
    };

    try {
      const response = await this.fetchWithTimeout(url, config);
      
      if (!response.ok) {
        const errorText = await response.text();
        let errorData = {};
        try {
          errorData = JSON.parse(errorText);
        } catch {
          errorData = { raw: errorText };
        }
        // FastAPI returns errors in 'detail' field, could be string or array
        let message = errorData.message || errorData.detail;
        if (Array.isArray(message)) {
          // FastAPI validation errors are arrays
          message = message.map(e => `${e.loc?.join('.')}: ${e.msg}`).join(', ');
        }
        console.error('API Error Response:', {
          status: response.status,
          statusText: response.statusText,
          url: url,
          errorData: JSON.stringify(errorData, null, 2),
          rawError: errorText,
        });
        throw {
          status: response.status,
          message: message || `خطأ في الاتصال ${response.status}`,
          data: errorData,
        };
      }

      return await response.json();
    } catch (error) {
      if (error.status) {
        throw error;
      }
      throw {
        status: 0,
        message: error.code === 'TIMEOUT' ? 'REQUEST_TIMEOUT' : (error.message || 'NETWORK_ERROR'),
        userMessage: error.userMessage || error.message || 'خطأ في الشبكة',
        isNetworkError: true,
      };
    }
  }

  // GET request
  async get(endpoint, options = {}) {
    return this.request(endpoint, { ...options, method: 'GET' });
  }

  // POST request
  async post(endpoint, data, options = {}) {
    return this.request(endpoint, {
      ...options,
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // PUT request
  async put(endpoint, data, options = {}) {
    return this.request(endpoint, {
      ...options,
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // DELETE request
  async delete(endpoint, options = {}) {
    return this.request(endpoint, { ...options, method: 'DELETE' });
  }

  // Auth endpoints
  async login(email, password) {
    // Clear any existing tokens before fresh login to avoid sending stale tokens
    tokenStorage.clearAll();

    console.log('API Service: Attempting login', {
      email,
      endpoint: '/user_management/user/login_user',
      baseUrl: this.baseUrl,
      fullUrl: `${this.baseUrl}/user_management/user/login_user`,
    });

    try {
      // Using PATCH method as per API spec
      const response = await this.request('/user_management/user/login_user', {
        method: 'PATCH',
        body: JSON.stringify({ email, password }),
      });

      console.log('API Service: Login successful', {
        hasAccessToken: !!response.access_token,
        hasEncryptedToken: !!response.encrypted_token,
      });

      // Store both tokens securely
      if (response.access_token && response.encrypted_token) {
        tokenStorage.setTokens(response.access_token, response.encrypted_token);
      }

      return response;
    } catch (error) {
      console.error('API Service: Login failed', {
        error,
        status: error.status,
        message: error.message,
      });
      throw error;
    }
  }

  async logout() {
    try {
      await this.post('/auth/logout');
    } finally {
      tokenStorage.clearAll();
    }
  }

  async validateSession() {
    return this.get('/auth/validate');
  }

  // Interview endpoints (matching documentation)

  // Get all interviews for authenticated candidate
  async getCandidateInterviews() {
    return this.get('/ai-talent/candidates/me/interviews');
  }

  // Get interview metadata and LiveKit connection info
  // If roomName is provided, request token for that specific room (for reconnection after refresh)
  async getInterviewMetadata(candidateId, interviewId = null, roomName = null) {
    let endpoint = `/ai-talent/interview/${candidateId}`;
    const params = [];
    if (interviewId) params.push(`interview_id=${interviewId}`);
    if (roomName) params.push(`room=${encodeURIComponent(roomName)}`);
    if (params.length > 0) {
      endpoint += '?' + params.join('&');
    }
    return this.get(endpoint);
  }

  // Start interview session
  // Room parameter ensures agent joins the same room as the frontend
  async startInterview(candidateId, roomName = null) {
    const endpoint = roomName
      ? `/ai-talent/interview/start/${candidateId}?room=${encodeURIComponent(roomName)}`
      : `/ai-talent/interview/start/${candidateId}`;
    return this.post(endpoint, {});
  }

  // Generate assessment for interview
  async generateAssessment(candidateId, roomName) {
    return this.post(`/ai-talent/interview/${candidateId}/generate-assessment`, {
      room_name: roomName,
    });
  }

  // End interview session
  async endInterview(candidateId, roomName = null) {
    const endpoint = `/ai-talent/interview/end/${candidateId}`;
    const params = roomName ? { room: roomName } : {};
    return this.post(endpoint, params);
  }

  // Video upload endpoints (chunked upload as per documentation)

  // Initialize video upload session
  async initializeVideoUpload(candidateId, videoMetadata) {
    return this.post(`/ai-talent/interview/${candidateId}/video/init`, videoMetadata);
  }

  // Upload single video chunk
  async uploadVideoChunk(candidateId, uploadId, chunkIndex, chunkData) {
    const formData = new FormData();
    formData.append('upload_id', uploadId);
    // Send chunk_index as string (matches reference implementation)
    formData.append('chunk_index', chunkIndex.toString());
    // Simple filename format (matches reference implementation)
    formData.append('chunk_data', chunkData, `chunk_${chunkIndex}`);

    console.log('Uploading chunk:', { uploadId, chunkIndex, filename: `chunk_${chunkIndex}` });

    const token = this.getAuthToken();
    const encryptedToken = this.getEncryptedToken();

    const url = `${this.baseUrl}/ai-talent/interview/${candidateId}/video/chunk`;
    console.log('Chunk upload URL:', url);
    console.log('Chunk data size:', chunkData?.size || 'undefined');

    // Adaptive timeout: at least 3 minutes, or enough time for a 256 Kbps connection
    // with 2x safety margin. This handles slow mobile/rural connections.
    const MIN_CHUNK_TIMEOUT = 180000; // 3 minutes minimum
    const chunkSizeBytes = chunkData?.size || 5 * 1024 * 1024;
    const CHUNK_UPLOAD_TIMEOUT = Math.max(MIN_CHUNK_TIMEOUT, Math.ceil((chunkSizeBytes / (32 * 1024)) * 1000 * 2));
    const response = await this.fetchWithTimeout(
      url,
      {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Encrypted-Token': encryptedToken,
          // Do NOT set Content-Type - browser will set it with boundary for FormData
        },
        body: formData,
      },
      CHUNK_UPLOAD_TIMEOUT
    );

    console.log('Chunk upload response:', { status: response.status, ok: response.ok, statusText: response.statusText });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error('Chunk upload error:', errorData);
      throw {
        status: response.status,
        message: errorData.detail?.detail || 'فشل في رفع جزء الفيديو',
        data: errorData,
      };
    }

    const result = await response.json();
    console.log('Chunk upload success:', result);
    return result;
  }

  // Complete video upload
  async completeVideoUpload(candidateId, uploadId, allowPartial = false) {
    const endpoint = `/ai-talent/interview/${candidateId}/video/complete`;
    const payload = {
      upload_id: uploadId,
      allow_partial: allowPartial,
    };
    console.log('CompleteVideoUpload request:', {
      endpoint,
      payload: JSON.stringify(payload),
    });
    return this.post(endpoint, payload);
  }

  // Update total_chunks after recording finishes (streaming upload correction)
  async updateVideoUploadChunks(candidateId, uploadId, actualTotalChunks) {
    return this.request(`/ai-talent/interview/${candidateId}/video/chunks`, {
      method: 'PATCH',
      body: JSON.stringify({ upload_id: uploadId, actual_total_chunks: actualTotalChunks }),
    });
  }

  // Check upload status (optional)
  async checkUploadStatus(candidateId, uploadId) {
    return this.get(`/ai-talent/interview/${candidateId}/video/status/${uploadId}`);
  }

  // Abort upload (optional)
  async abortVideoUpload(candidateId, uploadId, reason = 'User cancelled') {
    return this.post(`/ai-talent/interview/${candidateId}/video/abort`, {
      upload_id: uploadId,
      reason: reason,
    });
  }

  // Transcript endpoints
  async getTranscript(sessionId) {
    return this.get(`/interviews/${sessionId}/transcript`);
  }

  async sendMessage(sessionId, message) {
    return this.post(`/interviews/${sessionId}/messages`, { message });
  }
}

// Create singleton instance
const apiService = new ApiService();

export default apiService;

// Mock API for development (when backend is not available)
export const mockApi = {
  async login(email, password) {
    // Simulate network delay
    await new Promise(resolve => setTimeout(resolve, 1000));

    // Demo credentials check
    if (email === 'ali.hajhussein@iqarx.com' && password === 'Ai_testuser@123') {
      // Matching the real API TokenResponse structure
      const response = {
        message: 'Login Successful',
        encrypted_token: 'mock_encrypted_token_' + Date.now(),
        access_token: 'mock_jwt_token_' + Date.now(),
      };

      // Store tokens securely
      tokenStorage.setTokens(response.access_token, response.encrypted_token);

      return response;
    }

    throw {
      status: 401,
      message: 'بيانات الاعتماد غير صحيحة',
    };
  },

  async validateSession() {
    await new Promise(resolve => setTimeout(resolve, 500));
    const token = tokenStorage.getAccessToken();
    if (token && token.startsWith('mock_jwt_token_')) {
      return { valid: true };
    }
    throw { status: 401, message: 'جلسة غير صالحة' };
  },

  async uploadVideoChunk(sessionId, chunkData) {
    await new Promise(resolve => setTimeout(resolve, 200));
    console.log('Mock: Video chunk uploaded', { sessionId, size: chunkData.blob?.size });
    return { success: true, chunkId: `chunk_${Date.now()}` };
  },

  async uploadFinalVideo(sessionId, videoBlob) {
    await new Promise(resolve => setTimeout(resolve, 1000));
    console.log('Mock: Final video uploaded', { sessionId, size: videoBlob?.size });
    return { success: true, videoUrl: `https://mock-storage.com/videos/${sessionId}.webm` };
  },

  async endInterview(sessionId) {
    await new Promise(resolve => setTimeout(resolve, 500));
    return { success: true };
  },
};
